import { simpleGqlClient } from '@/graphql/simple-client.jsx';
import api from '@/api/axiosConfig.js';

const buildQuery = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.desde) params.set('fecha_desde', filters.desde);
  if (filters.hasta) params.set('fecha_hasta', filters.hasta);
  if (filters.tipo) params.set('tipo', filters.tipo);
  if (filters.sucursalId) params.set('sucursal_id', filters.sucursalId);
  if (filters.subcuentaId) params.set('subcuenta_id', filters.subcuentaId);
  if (filters.soloPendientes) params.set('solo_pendientes', '1');
  if (filters.soloVencidos) params.set('solo_vencidos', '1');
  if (filters.incluirCancelados) params.set('incluir_cancelados', '1');
  if (filters.tipoDocumento) params.set('tipo_documento', filters.tipoDocumento);
  if (filters.campoMovimiento) params.set('campo_movimiento', filters.campoMovimiento);
  if (filters.ordenMovimiento) params.set('orden_movimiento', filters.ordenMovimiento);
  if (filters.campoPendiente) params.set('campo_pendiente', filters.campoPendiente);
  if (filters.ordenPendiente) params.set('orden_pendiente', filters.ordenPendiente);
  if (filters.page) params.set('page', filters.page);
  if (filters.pageSize) params.set('page_size', filters.pageSize);
  return params.toString();
};

export const fetchCuentaCorrienteCliente = async (id, filters = {}) => {
  const query = buildQuery(filters);
  const response = await api.get(
    `/api/clientes/${id}/cuenta-corriente${query ? `?${query}` : ''}`,
  );
  return response.data?.data;
};

export const registrarAjusteSaldo = async (clienteId, input) => {
  const response = await api.post(`/api/clientes/${clienteId}/cuenta-corriente/ajustes`, input);
  return response.data?.data;
};

export const anularAjusteSaldo = async (clienteId, ajusteId, input) => {
  const response = await api.patch(`/api/clientes/${clienteId}/cuenta-corriente/ajustes/${ajusteId}/anular`, input);
  return response.data?.data;
};

export const aplicarAjusteCredito = async (clienteId, ajusteId, aplicaciones) => {
  const response = await api.post(`/api/clientes/${clienteId}/cuenta-corriente/ajustes/${ajusteId}/aplicaciones`, { aplicaciones });
  return response.data?.data;
};

export const listarAjustesCuentaCorriente = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.clienteId) params.set('cliente_id', filters.clienteId);
  if (filters.desde) params.set('fecha_desde', filters.desde);
  if (filters.hasta) params.set('fecha_hasta', filters.hasta);
  if (filters.tipo) params.set('tipo', filters.tipo);
  if (filters.sucursalId) params.set('sucursal_id', filters.sucursalId);
  if (filters.numero) params.set('numero', filters.numero);
  if (filters.referencia) params.set('referencia', filters.referencia);
  if (filters.texto) params.set('texto', filters.texto);
  if (filters.incluirCancelados) params.set('incluir_cancelados', '1');
  if (filters.page) params.set('page', filters.page);
  if (filters.pageSize) params.set('page_size', filters.pageSize);
  const response = await api.get(`/api/clientes/cuenta-corriente/ajustes${params.toString() ? `?${params}` : ''}`);
  return response.data?.data;
};

export const descargarAjusteCuentaPdf = async (clienteId, ajusteId, { preview = false } = {}) => {
  const response = await api.get(
    `/api/clientes/${clienteId}/cuenta-corriente/ajustes/${ajusteId}/pdf${preview ? '?preview=1' : ''}`,
    { responseType: 'blob' },
  );
  return {
    blob: response.data,
    filename: response.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/i)?.[1]
      || `ajuste-cuenta-corriente-AJ-${String(ajusteId).padStart(8, '0')}.pdf`,
  };
};

export const descargarResumenDeDeudaPdf = async (clienteId, ajusteId, { preview = false, fechaEmision, fechaVencimiento } = {}) => {
  const query = new URLSearchParams();
  if (preview) query.set('preview', '1');
  if (fechaEmision) query.set('fecha_emision', fechaEmision);
  if (fechaVencimiento) query.set('fecha_vencimiento', fechaVencimiento);
  const response = await api.get(`/api/clientes/${clienteId}/cuenta-corriente/ajustes/${ajusteId}/resumen-de-deuda.pdf${query.toString() ? `?${query.toString()}` : ''}`, { responseType: 'blob' });
  return { blob: response.data, filename: response.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/i)?.[1] || `resumen-de-deuda-${ajusteId}.pdf` };
};

export const descargarResumenGeneralCuentaPdf = async ({ desde = '', hasta = '', sucursalId = '', saldo = '' } = {}) => {
  const query = new URLSearchParams();
  if (saldo) query.set('saldo', saldo);
  if (desde) query.set('fecha_desde', desde);
  if (hasta) query.set('fecha_hasta', hasta);
  if (sucursalId) query.set('sucursal_id', sucursalId);
  const response = await api.get(
    `/api/clientes/cuenta-corriente/resumen-general/pdf${query.toString() ? `?${query}` : ''}`,
    { responseType: 'blob' },
  );
  return {
    blob: response.data,
    filename: response.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/i)?.[1]
      || `resumen-general-cuentas-${new Date().toISOString().slice(0, 10)}.pdf`,
  };
};

export const descargarResumenCuentaPdf = async (id, filters = {}, { preview = false } = {}) => {
  const query = new URLSearchParams(buildQuery(filters));
  if (preview) query.set('preview', '1');
  const response = await api.get(
    `/api/clientes/${id}/cuenta-corriente/pdf${query.toString() ? `?${query.toString()}` : ''}`,
    { responseType: 'blob' },
  );
  return {
    blob: response.data,
    filename: response.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/i)?.[1]
      || `resumen-cuenta-${id}.pdf`,
  };
};

export const descargarResumenCuentaCsv = async (id, filters = {}) => {
  const query = buildQuery(filters);
  const response = await api.get(
    `/api/clientes/${id}/cuenta-corriente/exportar${query ? `?${query}` : ''}`,
    { responseType: 'blob' },
  );
  return {
    blob: response.data,
    filename: response.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/i)?.[1]
      || `cuenta-corriente-${id}.csv`,
  };
};

export const fetchCreditoCliente = async (id, { sucursalId } = {}) => {
  const params = new URLSearchParams();
  if (sucursalId) params.set('sucursal_id', sucursalId);
  const response = await api.get(
    `/api/clientes/${id}/credito${params.toString() ? `?${params.toString()}` : ''}`,
  );
  return response.data?.data;
};


export const fetchCreditosEnRiesgo = async ({ sucursalId, limite = 50 } = {}) => {
  const query = `
    query($sucursalId: ID!, $limite: Int) {
      creditosEnRiesgo(sucursal_id: $sucursalId, limite: $limite) {
        cliente_id
        cliente_nombre
        limite_credito
        exposure
        deuda_vencida
        estado_credito
        pct_limite
      }
    }
  `;

  const result = await simpleGqlClient.query(query, { sucursalId, limite });
  if (result.errors?.length) throw new Error(result.errors[0]?.message || 'No se pudieron obtener créditos en riesgo');
  return result.data?.creditosEnRiesgo || [];
};

export const fetchClientesMorosos = async ({ sucursalId, limite = 50 } = {}) => {
  const query = `
    query($sucursalId: ID!, $limite: Int) {
      clientesMorosos(sucursal_id: $sucursalId, limite: $limite) {
        cliente_id
        cliente_nombre
        limite_credito
        exposure
        deuda_vencida
        estado_credito
        pct_limite
      }
    }
  `;

  const result = await simpleGqlClient.query(query, { sucursalId, limite });
  if (result.errors?.length) throw new Error(result.errors[0]?.message || 'No se pudieron obtener clientes morosos');
  return result.data?.clientesMorosos || [];
};
